import React, { useEffect, useState, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  ActivityIndicator, Platform, TextInput,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { OtaPolicyCard } from '../../src/components/admin/OtaPolicy';
import { api, apiPost, apiDelete } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { fs } from '../../src/utils/responsive';
import type { ColorTheme } from '../../src/utils/theme';

interface FirmwareVersion {
  readonly id: string;
  readonly version: string;
  readonly filename: string;
  readonly fileSize: number;
  readonly sha256: string;
  readonly releaseNotes: string | null;
  readonly isStable: boolean;
  readonly createdAt: string;
  /** 1.4 P04: 'dtwonder' | 'ws-8ro' */
  readonly family?: string;
}

const FAMILY_OPTIONS: { id: string; label: string }[] = [
  { id: 'dtwonder', label: 'dtwonder (RCP-Alpha-PoE)' },
  { id: 'ws-8ro', label: 'ws-8ro (RCP-Beta-PoE)' },
];

interface OtaStatus {
  readonly controllerId: string;
  readonly name: string | null;
  readonly currentVersion: string | null;
  readonly targetVersion: string | null;
  readonly otaStatus: string;
  readonly otaStartedAt: string | null;
  readonly isOnline: boolean;
}

/** Bytes per kilobyte for file size display */
const BYTES_PER_KB = 1024;

/** Number of SHA256 hex chars to display in the summary */
const SHA_PREVIEW_LENGTH = 12;

/** FirmwarePage - manages firmware uploads, OTA deployments, and version stability */
export default function FirmwarePage() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [firmware, setFirmware] = useState<FirmwareVersion[]>([]);
  const [otaStatus, setOtaStatus] = useState<OtaStatus[]>([]);
  const [otaTotal, setOtaTotal] = useState(0);
  const [otaPage, setOtaPage] = useState(1);
  const [otaSearch, setOtaSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showDeploy, setShowDeploy] = useState<string | null>(null);
  const [deployControllerIds, setDeployControllerIds] = useState('');

  // Upload form
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadStable, setUploadStable] = useState(false);
  const [uploadFamily, setUploadFamily] = useState('dtwonder');
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  /** Fetch a page of OTA deployment statuses. */
  const fetchOtaStatus = async (p = 1, searchOverride?: string) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      const s = searchOverride ?? otaSearch;
      if (s) params.set('search', s);
      const data = await api<{ statuses: OtaStatus[]; total: number }>(`/api/admin/firmware/status?${params}`);
      setOtaStatus(data.statuses);
      setOtaTotal(data.total);
      setOtaPage(p);
    } catch (err: any) {
      console.error('Failed to load OTA status:', err);
    }
  };

  /** Fetches firmware versions and active OTA deployment statuses in parallel */
  const fetchData = useCallback(async (signal?: AbortSignal) => {
    try {
      const [fwData] = await Promise.all([
        api<FirmwareVersion[]>('/api/admin/firmware'),
        fetchOtaStatus(1),
      ]);
      if (signal?.aborted) return;
      setFirmware(fwData);
    } catch (err: any) {
      if (!(signal?.aborted)) console.error('Failed to load firmware data:', err);
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchData(ac.signal); return () => ac.abort(); }, []);

  /** Uploads firmware binary via multipart form (web only) */
  const handleUpload = async () => {
    if (!uploadFile || !uploadVersion.trim()) {
      alert('Version and file are required');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('version', uploadVersion.trim());
      formData.append('releaseNotes', uploadNotes.trim());
      formData.append('isStable', String(uploadStable));
      formData.append('family', uploadFamily);

      /* Use fetch directly for multipart upload — api() doesn't support FormData */
      const { getToken, getCsrfToken } = await import('../../src/api/client');
      const { config } = await import('../../src/config');
      const IS_WEB = Platform.OS === 'web';
      const headers: Record<string, string> = {};
      if (IS_WEB) {
        headers['X-Client-Type'] = 'web';
        const csrf = getCsrfToken();
        if (csrf) headers['X-CSRF-Token'] = csrf;
      } else {
        const token = getToken();
        if (token) headers['Authorization'] = `Bearer ${token}`;
      }
      const resp = await fetch(`${config.apiUrl}/api/admin/firmware/upload`, {
        method: 'POST',
        headers,
        body: formData,
        ...(IS_WEB ? { credentials: 'include' as RequestCredentials } : {}),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || 'Upload failed');
      }

      setShowUpload(false);
      setUploadVersion('');
      setUploadNotes('');
      setUploadStable(false);
      setUploadFile(null);
      fetchData();
      alert('Firmware uploaded successfully!');
    } catch (err: any) {
      alert(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /** Deploys a firmware version OTA to specified controller IDs */
  const handleDeploy = async (version: string) => {
    const ids = deployControllerIds.split(',').map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) {
      alert('Enter at least one controller ID');
      return;
    }
    setDeploying(true);
    try {
      let result: { results: Array<{ sent: boolean }> };
      try {
        result = await apiPost<{ results: Array<{ sent: boolean }> }>('/api/admin/firmware/deploy', { version, controllerIds: ids });
      } catch (err: any) {
        // 1.4 P04: the family gate returns 409 with the offending ids.
        const d = (err?.details ?? {}) as Record<string, any>;
        const parts: string[] = [];
        if (Array.isArray(d.wrongFamily) && d.wrongFamily.length) parts.push(`wrong family: ${d.wrongFamily.map((w: any) => `${w.id} (${w.model})`).join(', ')}`);
        if (Array.isArray(d.mismatched) && d.mismatched.length) parts.push(`model mismatch: ${d.mismatched.map((w: any) => `${w.id} reports ${w.reportedModel}`).join(', ')}`);
        if (Array.isArray(d.missing) && d.missing.length) parts.push(`unknown ids: ${d.missing.join(', ')}`);
        throw new Error(parts.length ? `${err.message} — ${parts.join('; ')}` : (err.message || 'Deploy failed'));
      }
      setShowDeploy(null);
      setDeployControllerIds('');
      fetchData();
      const successCount = result.results?.filter((r) => r.sent).length || 0;
      alert(`OTA deployed to ${successCount}/${ids.length} controllers`);
    } catch (err: any) {
      alert(err.message || 'Deploy failed');
    } finally {
      setDeploying(false);
    }
  };

  /** Toggles stable flag for a firmware version */
  const toggleStable = async (version: string, currentlyStable: boolean) => {
    try {
      await api(`/api/admin/firmware/${version}/stable`, { method: 'PUT', body: { isStable: !currentlyStable } });
      fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to update stable flag');
    }
  };

  /** Deletes a firmware version */
  const deleteFirmware = async (version: string) => {
    if (!confirm(`Delete firmware v${version}? This cannot be undone.`)) return;
    try {
      await apiDelete(`/api/admin/firmware/${version}`);
      fetchData();
    } catch (err: any) {
      alert(err.message || 'Failed to delete firmware');
    }
  };

  const renderFirmware = ({ item }: { item: FirmwareVersion }) => (
    <View style={styles.fwCard}>
      <View style={styles.fwHeader}>
        <View style={styles.fwLeft}>
          <Text style={styles.fwVersion}>v{item.version}</Text>
          <View style={styles.fwBadgeFamily}><Text style={styles.fwBadgeFamilyText}>{item.family || 'dtwonder'}</Text></View>
          {item.isStable && (
            <View style={styles.stableBadge}>
              <Text style={styles.stableBadgeText}>Stable</Text>
            </View>
          )}
        </View>
        <Text style={styles.fwDate}>{new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</Text>
      </View>
      <View style={styles.fwMeta}>
        <Text style={styles.fwMetaText}>
          {(item.fileSize / BYTES_PER_KB).toFixed(0)} KB | SHA256: {item.sha256.substring(0, SHA_PREVIEW_LENGTH)}...
        </Text>
      </View>
      {item.releaseNotes && (
        <Text style={styles.fwNotes}>{item.releaseNotes}</Text>
      )}
      <View style={styles.fwActions}>
        <TouchableOpacity style={styles.actionBtn} onPress={() => toggleStable(item.version, item.isStable)}>
          <Ionicons name={item.isStable ? 'close-circle-outline' : 'checkmark-circle-outline'} size={16} color={item.isStable ? '#d97706' : '#059669'} />
          <Text style={[styles.actionText, { color: item.isStable ? '#d97706' : '#059669' }]}>
            {item.isStable ? 'Unmark Stable' : 'Mark Stable'}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionBtn}
          onPress={() => { setShowDeploy(showDeploy === item.version ? null : item.version); setDeployControllerIds(''); }}
        >
          <Ionicons name="cloud-upload-outline" size={16} color={colors.primary} />
          <Text style={[styles.actionText, { color: colors.primary }]}>Deploy OTA</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.actionBtn} onPress={() => deleteFirmware(item.version)}>
          <Ionicons name="trash-outline" size={16} color="#dc2626" />
          <Text style={[styles.actionText, { color: '#dc2626' }]}>Delete</Text>
        </TouchableOpacity>
      </View>
      {showDeploy === item.version && (
        <View style={styles.deployForm}>
          <Text style={styles.deployLabel}>Controller IDs (comma-separated):</Text>
          <TextInput
            style={[styles.input, { color: colors.text }]}
            placeholder="RPC-abc123..., RPC-def456..."
            placeholderTextColor={colors.textLight}
            value={deployControllerIds}
            onChangeText={setDeployControllerIds}
            multiline
            maxLength={500}
          />
          <TouchableOpacity
            style={[styles.deployBtn, deploying && { opacity: 0.6 }]}
            onPress={() => handleDeploy(item.version)}
            disabled={deploying}
          >
            {deploying ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.deployBtnText}>Deploy to Controllers</Text>
            )}
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <Text style={styles.pageTitle}>Firmware Management</Text>
      <TouchableOpacity
        style={styles.uploadBtn}
        onPress={() => setShowUpload(!showUpload)}
      >
        <Ionicons name="cloud-upload" size={18} color="#fff" />
        <Text style={styles.uploadBtnText}>Upload Firmware</Text>
      </TouchableOpacity>

      {/* Upload Form */}
      {showUpload && (
        <View style={styles.uploadCard}>
          <Text style={styles.uploadTitle}>Upload New Firmware</Text>
          <View style={styles.formRow}>
            <Text style={styles.label}>Version *</Text>
            <TextInput
              style={[styles.input, { color: colors.text }]}
              placeholder="e.g. 1.1.0"
              placeholderTextColor={colors.textLight}
              value={uploadVersion}
              onChangeText={setUploadVersion}
              maxLength={20}
            />
          </View>
          <View style={styles.formRow}>
            <Text style={styles.label}>Release Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea, { color: colors.text }]}
              placeholder="What changed..."
              placeholderTextColor={colors.textLight}
              value={uploadNotes}
              onChangeText={setUploadNotes}
              multiline
              maxLength={500}
            />
          </View>
          <View style={styles.formRow}>
            <Text style={styles.label}>Binary (.bin)</Text>
            {Platform.OS === 'web' ? (
              <input
                type="file"
                accept=".bin"
                onChange={(e: any) => setUploadFile(e.target.files?.[0] || null)}
                style={{ fontSize: 14, color: colors.text }}
              />
            ) : (
              <Text style={styles.noFileText}>File upload available on web only</Text>
            )}
          </View>
          <View style={styles.formRow}>
            <Text style={styles.label}>Firmware family * (which board this image is built for)</Text>
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
              {FAMILY_OPTIONS.map(f => (
                <TouchableOpacity
                  key={f.id}
                  onPress={() => setUploadFamily(f.id)}
                  style={{
                    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1,
                    borderColor: uploadFamily === f.id ? colors.primary : colors.border,
                    backgroundColor: uploadFamily === f.id ? colors.primary + '20' : 'transparent',
                  }}
                >
                  <Text style={{ color: uploadFamily === f.id ? colors.primary : colors.textSecondary, fontSize: 12, fontWeight: '600' }}>{f.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          <TouchableOpacity
            style={styles.checkRow}
            onPress={() => setUploadStable(!uploadStable)}
          >
            <Ionicons
              name={uploadStable ? 'checkbox' : 'square-outline'}
              size={20}
              color={colors.primary}
            />
            <Text style={styles.checkLabel}>Mark as stable</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.submitBtn, uploading && { opacity: 0.6 }]}
            onPress={handleUpload}
            disabled={uploading}
          >
            {uploading ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.submitBtnText}>Upload</Text>
            )}
          </TouchableOpacity>
        </View>
      )}

      {/* OTA Deployment Status */}
      {(otaStatus.length > 0 || otaTotal > 0 || otaSearch !== '') && (
        <>
          <Text style={styles.sectionTitle}>Active OTA Deployments</Text>
          <AdminToolbar
            search={otaSearch}
            onSearchChange={setOtaSearch}
            onSearch={() => fetchOtaStatus(1)}
            total={otaTotal}
            page={otaPage}
            limit={10}
            onPageChange={(p) => fetchOtaStatus(p)}
            csvEndpoint={`/api/admin/firmware/status${otaSearch ? `?search=${encodeURIComponent(otaSearch)}` : ''}`}
            csvFilename="ota-status.csv"
            label="deployment"
          />
          <View style={styles.otaCard}>
            {otaStatus.length === 0 && (
              <Text style={styles.emptyText}>No OTA deployments found</Text>
            )}
            {otaStatus.map(s => (
              <View key={s.controllerId} style={styles.otaRow}>
                <View style={[styles.otaDot, { backgroundColor: s.isOnline ? '#059669' : '#dc2626' }]} />
                <View style={{ flex: 1 }}>
                  <Text style={styles.otaName} numberOfLines={1}>{s.name || s.controllerId.substring(0, 16)}</Text>
                  {s.otaStartedAt && (
                    <Text style={styles.otaStarted}>
                      started {new Date(s.otaStartedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </Text>
                  )}
                </View>
                <Text style={styles.otaVersions}>{s.currentVersion || '?'} → {s.targetVersion}</Text>
                <View style={[styles.otaStatusBadge, { backgroundColor: s.otaStatus === 'pending' ? '#fef3c7' : '#dbeafe' }]}>
                  <Text style={[styles.otaStatusText, { color: s.otaStatus === 'pending' ? '#d97706' : '#3b82f6' }]}>
                    {s.otaStatus}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </>
      )}

      {/* 1.4 P21: rollout policy + event log */}
      <OtaPolicyCard />

      {/* Firmware Versions */}
      <Text style={styles.sectionTitle}>Firmware Versions</Text>
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />}
          data={firmware}
          keyExtractor={(item) => item.id}
          renderItem={renderFirmware}
          contentContainerStyle={{ paddingBottom: spacing.xl }}
          ListEmptyComponent={
            <Text style={styles.emptyText}>No firmware versions uploaded yet</Text>
          }
        />
      )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  pageTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.sm },
  uploadBtn: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs, alignSelf: 'flex-start',
    backgroundColor: c.primary, paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md, marginBottom: spacing.md,
  },
  uploadBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  uploadCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.md, borderWidth: 1, borderColor: c.borderLight,
  },
  uploadTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  formRow: { marginBottom: spacing.sm },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary, marginBottom: 4 },
  input: {
    backgroundColor: c.background, borderWidth: 1, borderColor: c.borderLight,
    borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 8,
    fontSize: fontSize.sm,
  },
  textArea: { minHeight: 60, textAlignVertical: 'top' },
  noFileText: { fontSize: fontSize.sm, color: c.textLight },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md },
  checkLabel: { fontSize: fontSize.sm, color: c.text },
  submitBtn: {
    backgroundColor: c.primary, paddingVertical: spacing.sm, borderRadius: borderRadius.md,
    alignItems: 'center',
  },
  submitBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.md },
  sectionTitle: {
    fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary,
    textTransform: 'uppercase', letterSpacing: 1, marginBottom: spacing.sm, marginTop: spacing.sm,
  },
  otaCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight,
    marginBottom: spacing.md, overflow: 'hidden',
  },
  otaRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingVertical: 8, paddingHorizontal: spacing.md,
    borderBottomWidth: 1, borderBottomColor: c.borderLight,
  },
  otaDot: { width: 8, height: 8, borderRadius: 4 },
  otaName: { fontSize: fontSize.sm, color: c.text, fontWeight: fontWeight.medium },
  otaStarted: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  otaVersions: { fontSize: fontSize.xs, color: c.textSecondary, fontFamily: 'monospace' },
  otaStatusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 },
  otaStatusText: { fontSize: fs(10), fontWeight: fontWeight.semibold },
  fwCard: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  fwHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  fwLeft: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  fwVersion: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  stableBadge: { backgroundColor: '#dcfce7', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4 },
  fwBadgeFamily: { backgroundColor: c.primary + '20', paddingHorizontal: 8, paddingVertical: 2, borderRadius: 4, marginRight: 6 },
  fwBadgeFamilyText: { fontSize: fs(10), color: c.primary, fontWeight: fontWeight.semibold },
  stableBadgeText: { fontSize: fs(10), color: '#059669', fontWeight: fontWeight.semibold },
  fwDate: { fontSize: fontSize.xs, color: c.textSecondary },
  fwMeta: { marginTop: 4 },
  fwMetaText: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace' },
  fwNotes: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },
  fwActions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.sm },
  actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  actionText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium },
  deployForm: { marginTop: spacing.sm, paddingTop: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  deployLabel: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: 4 },
  deployBtn: {
    backgroundColor: c.primary, paddingVertical: 8, borderRadius: borderRadius.sm,
    alignItems: 'center', marginTop: spacing.sm,
  },
  deployBtnText: { color: '#fff', fontWeight: fontWeight.semibold, fontSize: fontSize.sm },
  emptyText: { textAlign: 'center', color: c.textSecondary, paddingVertical: spacing.xl, fontSize: fontSize.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
